/**
 * Invoice_Earn entry (Req 14, 28.2). Online only (Req 28.4). The result is `posted`,
 * `pending_approval` (above the tenant's maker-checker threshold) or posted with
 * `no_matching_rule` (0 points); a repeated invoice number answers `duplicate_invoice`.
 */
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import { asApiError, callOperation } from '@macksons/web-client';
import { useT } from '../hooks';
import { isInvoiceNo, lkrToMinor } from '../scanner/inputs';
import { useServices } from '../services';
import type { InvoiceEarnResult, MemberLookupResult } from '../types';
import { OnlineOnlyNotice, ResultCard, useErrorText, useIdempotencyKey, useOnline, type Feedback } from './ui';

type FieldErrors = Partial<Record<'invoiceNo' | 'amount', string>>;

export function InvoicePanel({ member, outletId }: { member: MemberLookupResult; outletId: string }) {
  const t = useT();
  const errorText = useErrorText();
  const { client } = useServices();
  const online = useOnline();
  const id = useId();
  const idem = useIdempotencyKey();
  const invoiceRef = useRef<HTMLInputElement>(null);
  const amountRef = useRef<HTMLInputElement>(null);
  const [invoiceNo, setInvoiceNo] = useState('');
  const [amount, setAmount] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState<Feedback>();
  const [result, setResult] = useState<InvoiceEarnResult | undefined>();
  const nextRef = useRef<HTMLButtonElement>(null);
  const prevResult = useRef<InvoiceEarnResult | undefined>(undefined);
  // Focus follows the flow: "Next invoice" after a result, the invoice field after it.
  useEffect(() => {
    if (result) nextRef.current?.focus();
    else if (prevResult.current) invoiceRef.current?.focus();
    prevResult.current = result;
  }, [result]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const inv = invoiceNo.trim();
    const amountMinor = lkrToMinor(amount);
    const next: FieldErrors = {};
    if (!isInvoiceNo(inv)) next.invoiceNo = t('operator.invoice.invalidInvoiceNo');
    if (!amountMinor) next.amount = t('operator.invoice.invalidAmount');
    setErrors(next);
    if (next.invoiceNo || !amountMinor) {
      (next.invoiceNo ? invoiceRef : amountRef).current?.focus();
      return;
    }
    const body = { memberId: member.memberId, outletId, invoiceNo: inv, amountMinor };
    setBusy(true);
    setFeedback({ tone: 'info', text: t('operator.invoice.submitting') });
    try {
      const res = await callOperation<InvoiceEarnResult>(client, 'createInvoiceEarn', {
        body,
        headers: { 'Idempotency-Key': idem.keyFor(JSON.stringify(body)) },
      });
      idem.reset();
      setResult(res);
      const count = Number(res.points);
      setFeedback({
        tone: res.reason === 'no_matching_rule' ? 'info' : 'ok',
        text:
          res.status === 'pending_approval'
            ? t('operator.invoice.pending', { invoiceNo: inv, count })
            : res.reason === 'no_matching_rule'
              ? t('errors.no_matching_rule')
              : t('operator.invoice.posted', { invoiceNo: inv, count }),
      });
    } catch (cause) {
      const err = asApiError(cause);
      setFeedback({ tone: 'error', text: errorText(err) });
      // A final answer for this payload: a corrected retry gets a new key.
      if (!err.retryable) idem.reset();
      if (err.code === 'duplicate_invoice') invoiceRef.current?.focus();
    } finally {
      setBusy(false);
    }
  };
  const next = () => {
    setResult(undefined);
    setInvoiceNo('');
    setAmount('');
    setFeedback(undefined);
  };
  const disabled = !online || busy || result !== undefined;
  const points =
    result && result.reason !== 'no_matching_rule' ? t('common.points', { count: Number(result.points) }) : undefined;
  return (
    <div className="form-panel">
      <h3>{t('invoice.title')}</h3>
      {!online && <OnlineOnlyNotice />}
      <ResultCard feedback={feedback} points={points} testId="invoice" />
      <form onSubmit={(e) => void submit(e)} noValidate className="form">
        <div className="field">
          <label htmlFor={`${id}-inv`}>{t('invoice.invoiceNo')}</label>
          <input
            ref={invoiceRef}
            id={`${id}-inv`}
            className="input"
            value={invoiceNo}
            onChange={(e) => setInvoiceNo(e.target.value)}
            aria-invalid={errors.invoiceNo !== undefined}
            aria-describedby={errors.invoiceNo ? `${id}-inv-err` : undefined}
            autoComplete="off"
            maxLength={64}
            disabled={disabled}
          />
          {errors.invoiceNo && (
            <p id={`${id}-inv-err`} className="field__error">
              {errors.invoiceNo}
            </p>
          )}
        </div>
        <div className="field">
          <label htmlFor={`${id}-amt`}>{t('invoice.amount')}</label>
          <input
            ref={amountRef}
            id={`${id}-amt`}
            className="input"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            inputMode="decimal"
            aria-invalid={errors.amount !== undefined}
            aria-describedby={errors.amount ? `${id}-amt-err` : undefined}
            autoComplete="off"
            disabled={disabled}
          />
          {errors.amount && (
            <p id={`${id}-amt-err`} className="field__error">
              {errors.amount}
            </p>
          )}
        </div>
        <div className="sticky-action">
          {result ? (
            <button ref={nextRef} type="button" className="button button--primary button--block" onClick={next} data-testid="invoice-next">
              {t('operator.invoice.next')}
            </button>
          ) : (
            <>
              <button type="submit" className="button button--primary button--block" disabled={disabled}>
                {t('operator.invoice.submit')}
              </button>
              {!online && (
                <p className="hint" data-testid="invoice-disabled-hint">
                  {t('operator.offline.needsConnection')}
                </p>
              )}
            </>
          )}
        </div>
      </form>
    </div>
  );
}
